/**
 * Calendar - Переиспользуемый компонент календаря
 *
 * Отображает события (встречи, звонки, МК) для конкретной организации.
 * Используется в CalendarWidget на карточке организации.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Group,
  SegmentedControl,
  Text,
  Loader,
  Center,
  Paper,
  Alert,
  Button,
  Modal,
  Checkbox,
} from '@mantine/core';
import { MonthPicker } from '@mantine/dates';
import {
  IconPhone,
  IconUsers,
  IconSchool,
  IconFilter,
  IconAlertCircle,
  IconRefresh,
} from '@tabler/icons-react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import ruLocale from '@fullcalendar/core/locales/ru';
import tokens from '../../theme/designTokens';
import EventDetailsModal from './EventDetailsModal';
import './calendar-styles.css';

// Типы событий для фильтра
const EVENT_TYPES = [
  { value: 'all', label: 'Все', icon: IconFilter },
  { value: 'meeting', label: 'Встречи', icon: IconUsers },
  { value: 'call', label: 'Звонки', icon: IconPhone },
  { value: 'master_class', label: 'МК', icon: IconSchool },
];

const Calendar = ({ organizationId, userId }) => {
  const calendarRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [eventFilter, setEventFilter] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailsModalOpened, setDetailsModalOpened] = useState(false);
  const [summary, setSummary] = useState({ total: 0, meetings: 0, calls: 0, master_classes: 0 });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthPickerOpened, setMonthPickerOpened] = useState(false);
  const [showCancelled, setShowCancelled] = useState(true);
  const [showCompleted, setShowCompleted] = useState(true);

  // Форматирование даты для заголовка
  const formatCurrentPeriod = useCallback(() => {
    if (!calendarRef.current) {
      return currentDate.toLocaleString('ru-RU', { month: 'long', year: 'numeric' });
    }
    const calendarApi = calendarRef.current.getApi();
    const view = calendarApi.view;
    const start = view.currentStart;
    const end = view.currentEnd;

    // Для вида месяца показываем только месяц и год
    if (view.type === 'dayGridMonth') {
      return start.toLocaleString('ru-RU', { month: 'long', year: 'numeric' });
    }

    // Для недели/дня показываем диапазон
    const startMonth = start.toLocaleString('ru-RU', { month: 'short', day: 'numeric' });
    const endDate = new Date(end);
    endDate.setDate(endDate.getDate() - 1);
    const endMonth = endDate.toLocaleString('ru-RU', { month: 'short', day: 'numeric', year: 'numeric' });

    return `${startMonth} – ${endMonth}`;
  }, [currentDate]);

  // Обновление текущей даты при изменении вида календаря
  const handleDatesSet = (dateInfo) => {
    setCurrentDate(dateInfo.start);
  };

  // Переход к выбранному месяцу
  const handleMonthSelect = (date) => {
    if (calendarRef.current && date) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(date);
      setCurrentDate(date);
    }
    setMonthPickerOpened(false);
  };

  // Обновление текста кнопки при изменении даты
  useEffect(() => {
    const updateButtonText = () => {
      // Используем уникальный селектор для виджета организации
      const container = calendarRef.current?.elRef?.current;
      if (!container) return;
      const btn = container.querySelector('.fc-customTitle-button');
      if (btn) {
        btn.innerHTML = `<span style="display: flex; align-items: center; gap: 6px;">${formatCurrentPeriod()} <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg></span>`;
      }
    };
    const timer = setTimeout(updateButtonText, 50);
    return () => clearTimeout(timer);
  }, [formatCurrentPeriod]);

  // Загрузка событий
  const fetchEvents = useCallback(async (fetchInfo, successCallback, failureCallback) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        start: fetchInfo.startStr,
        end: fetchInfo.endStr,
      });

      // Добавляем фильтр по организации или пользователю
      if (organizationId) {
        params.append('organization_id', organizationId);
      }
      if (userId) {
        params.append('user_id', userId);
      }

      // Добавляем фильтр по типу (если не "all")
      if (eventFilter !== 'all') {
        params.append('event_types', eventFilter);
      }

      const response = await fetch(`/api/calendar/events?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('У вас нет доступа к календарю');
        }
        throw new Error('Не удалось загрузить события');
      }

      const data = await response.json();
      setSummary(data.summary);

      // Преобразуем в формат FullCalendar
      let calendarEvents = data.events.map((event) => ({
        id: event.id,
        title: event.title,
        start: event.start,
        end: event.end,
        backgroundColor: event.color,
        borderColor: event.color,
        extendedProps: {
          type: event.type,
          status: event.status,
          organization: event.organization,
          details: event.details,
        },
      }));

      // Фильтрация по статусам
      calendarEvents = calendarEvents.filter((event) => {
        const status = event.extendedProps.status;
        if (!showCancelled && status === 'cancelled') {
          return false;
        }
        if (!showCompleted && status === 'completed') {
          return false;
        }
        return true;
      });

      successCallback(calendarEvents);
    } catch (err) {
      console.error('Failed to fetch calendar events:', err);
      setError(err.message);
      failureCallback(err);
    } finally {
      setLoading(false);
    }
  }, [organizationId, userId, eventFilter, showCancelled, showCompleted]);

  // Обработчик клика по событию
  const handleEventClick = (clickInfo) => {
    setSelectedEvent({
      id: clickInfo.event.id,
      title: clickInfo.event.title,
      start: clickInfo.event.start,
      end: clickInfo.event.end,
      ...clickInfo.event.extendedProps,
    });
    setDetailsModalOpened(true);
  };

  // Обновление событий при изменении фильтра типов
  const handleFilterChange = (value) => {
    setEventFilter(value);
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.refetchEvents();
    }
  };

  // Обновление событий при изменении фильтров статусов
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.refetchEvents();
    }
  }, [showCancelled, showCompleted]);

  // Ручное обновление
  const handleRefresh = () => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.refetchEvents();
    }
  };

  return (
    <>
      {/* Статистика и фильтры */}
      <Group justify="space-between" align="center" wrap="wrap" gap="sm" mb="sm">
        <Text c="dimmed" size="sm">
          Встречи: {summary.meetings} | Звонки: {summary.calls} | МК: {summary.master_classes}
        </Text>

        <Group gap="sm" wrap="wrap">
          <Button
            variant="light"
            leftSection={<IconRefresh size={14} />}
            onClick={handleRefresh}
            loading={loading}
            size="xs"
          >
            Обновить
          </Button>

          <SegmentedControl
            value={eventFilter}
            onChange={handleFilterChange}
            size="xs"
            radius="xl"
            data={EVENT_TYPES.map((t) => ({
              value: t.value,
              label: (
                <Group gap={4} wrap="nowrap">
                  <t.icon size={14} />
                  <span>{t.label}</span>
                </Group>
              ),
            }))}
          />
        </Group>
      </Group>

      {/* Фильтр по статусам */}
      <Group justify="flex-end" gap="md" mb="sm">
        <Checkbox
          label="Отменённые"
          checked={showCancelled}
          onChange={(e) => setShowCancelled(e.currentTarget.checked)}
          size="xs"
          radius="xl"
        />
        <Checkbox
          label="Завершённые"
          checked={showCompleted}
          onChange={(e) => setShowCompleted(e.currentTarget.checked)}
          size="xs"
          radius="xl"
        />
      </Group>

      {/* Ошибка */}
      {error && (
        <Alert
          icon={<IconAlertCircle size={14} />}
          title="Ошибка"
          color="red"
          variant="light"
          withCloseButton
          onClose={() => setError(null)}
          mb="sm"
        >
          {error}
        </Alert>
      )}

      {/* Календарь */}
      <Paper
        radius="xl"
        p="sm"
        style={{
          border: `1px solid ${tokens.colors.neutral[3]}`,
          position: 'relative',
          minHeight: '450px',
          overflow: 'hidden',
        }}
      >
        {loading && (
          <Center
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255,255,255,0.8)',
              zIndex: 10,
            }}
          >
            <Loader size="md" />
          </Center>
        )}

        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          locale={ruLocale}
          headerToolbar={{
            left: 'prev,next today',
            center: 'customTitle',
            right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
          }}
          customButtons={{
            customTitle: {
              text: '',
              click: () => setMonthPickerOpened(true),
            },
          }}
          buttonText={{
            today: 'Сегодня',
            month: 'Месяц',
            week: 'Неделя',
            day: 'День',
            list: 'Список',
          }}
          events={fetchEvents}
          eventClick={handleEventClick}
          datesSet={handleDatesSet}
          height="auto"
          dayMaxEvents={3}
          eventDisplay="block"
          eventTimeFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          }}
          slotMinTime="07:00:00"
          slotMaxTime="21:00:00"
          allDaySlot={false}
          nowIndicator={true}
          weekends={true}
          firstDay={1}
          eventDidMount={(info) => {
            const status = info.event.extendedProps.status;
            if (status === 'completed') {
              info.el.style.opacity = '0.7';
            } else if (status === 'cancelled') {
              info.el.style.opacity = '0.6';
              info.el.classList.add('fc-event-cancelled');
            } else if (status === 'missed') {
              info.el.style.border = '2px solid #C23934';
            }
          }}
        />
      </Paper>

      {/* Модальное окно деталей события */}
      <EventDetailsModal
        opened={detailsModalOpened}
        onClose={() => setDetailsModalOpened(false)}
        event={selectedEvent}
      />

      {/* Модальное окно выбора месяца */}
      <Modal
        opened={monthPickerOpened}
        onClose={() => setMonthPickerOpened(false)}
        title="Выберите месяц"
        centered
        size="auto"
      >
        <MonthPicker
          value={currentDate}
          onChange={handleMonthSelect}
          locale="ru"
        />
      </Modal>
    </>
  );
};

Calendar.propTypes = {
  organizationId: PropTypes.number,  // Для виджета на карточке организации
  userId: PropTypes.number,          // Для виджета Мой день
};

export default Calendar;
